import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, FileSignature, Users } from 'lucide-react';
import { GroupContractItemDto } from 'api/contract';
import { IdLabel } from 'api/contract';
import { Sizes } from 'components/forms/NumberSelector';
import { Button } from 'components/ui/Button';
import MoreBubble from 'components/ui/Bubble';
import { EyeIcon } from 'components/icons/Icons';
import { DropdownButton } from 'components/navigation/Dropdown/GenericDropdownButton';
import { NewGroupContractTypeModal } from 'back-office/features/group-contract-process/components/NewGroupContractTypeModal';
import { GroupContractStepIdentifier } from 'back-office/features/group-contract-process/types/enums';
import {
  BROKER_PROCESS_PREFIX,
  buildProcessRoute,
} from 'back-office/features/group-contract-process/utils/getRouteByStep';
import { NsKey } from 'enums/NsKey';
import { useDialog } from 'hooks/useDialog';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { contractsAPI } from 'services/swagger-api';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';

// Codes stables du référentiel StatutsContrats (alignés sur Codes.StatutsContrats côté back).
// Exportés pour l'onglet Contrats collectifs de la fiche client (PersonGroupContracts).
export const STATUT_BROUILLON_CODE = 'B';
export const STATUT_EN_COURS_CODE = 'EC';

export const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const STATUS_STYLES: Record<string, string> = {
  brouillon:    'bg-gray-100 text-gray-700 border-gray-300',
  'en cours':   'bg-green-100 text-green-700 border-green-300',
  actif:        'bg-green-100 text-green-700 border-green-300',
  'en attente': 'bg-yellow-100 text-yellow-700 border-yellow-300',
  expiré:       'bg-red-100 text-red-700 border-red-300',
  expire:       'bg-red-100 text-red-700 border-red-300',
  suspendu:     'bg-orange-100 text-orange-700 border-orange-300',
  résilié:      'bg-red-100 text-red-700 border-red-300',
  resilie:      'bg-red-100 text-red-700 border-red-300',
};

export const renderStatusBadge = (status: string) => {
  if (!status) return <span className="text-gray-400">-</span>;
  const className = STATUS_STYLES[status.toLowerCase()]
    || 'bg-gray-100 text-gray-700 border-gray-300';
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${className}`}>
      {status}
    </span>
  );
};

export default function GroupContractTable() {
  // Liste contrats de groupe : actions Courtier + Plateforme (matrice centrale).
  // La compagnie crée ses contrats de portée organisationnelle depuis cette liste.
  const canGroupActions = useRoleCan(Capability.GroupContractActions);
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  const { openBlankDialog, closeDialog } = useDialog();
  const navigate = useNavigate();
  const handleCreate = useCallback(() => {
    openBlankDialog(
      { size: Sizes['5xl'] },
      <NewGroupContractTypeModal onClose={closeDialog} mode="broker" />,
    );
  }, [openBlankDialog, closeDialog]);

  const handleView = useCallback((groupContract: GroupContractItemDto) => {
    // Contrat actif : page de détail (même page que le back-office admin).
    if (groupContract.statusCode === STATUT_EN_COURS_CODE) {
      navigate(`/mes-contrats-collectifs/${groupContract.id}`);
      return;
    }
    const isBrouillon = groupContract.statusCode === STATUT_BROUILLON_CODE;
    navigate(
      buildProcessRoute(
        groupContract.id,
        isBrouillon
          ? GroupContractStepIdentifier.Subscriber
          : GroupContractStepIdentifier.Contractualization,
        BROKER_PROCESS_PREFIX,
      ),
    );
  }, [navigate]);

  const fetchGroupContracts = useCallback(async (params: any) => {
    const response = await contractsAPI.getGroupContractsCourtier({
      getGenericListCommandDto: {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        searchString: params.searchString || '',
        sorting: params.sorting,
        filters: { ...(params.filters || {}) },
      },
    });

    return {
      items: response.items,
      totalItems: response.totalCount,
      totalPages: Math.ceil(response.totalCount / params.pageSize),
      currentPage: params.pageNumber,
    };
  }, []);

  return (
    <ServerTable<GroupContractItemDto>
      apiConfig={{ fetchData: fetchGroupContracts }}
      columns={[
        {
          key: 'contractIdentifier',
          label: 'Identifiant',
          sortable: true,
          width: '200px',
          render: (value: any) =>
            value ? (
              <span className="font-mono font-semibold text-gray-900">{String(value)}</span>
            ) : (
              <span className="text-gray-400 italic text-sm">Brouillon non finalisé</span>
            ),
        },
        {
          key: 'status',
          label: 'Statut',
          sortable: true,
          width: '120px',
          render: (value: any) => renderStatusBadge(String(value || '')),
        },
        {
          key: 'isMaster',
          label: 'Type',
          sortable: true,
          align: 'center',
          width: '110px',
          render: (value: any) =>
            value ? (
              <div className="flex items-center justify-center gap-2">
                <Crown className="text-yellow-600" size={16} />
                <span className="text-sm font-medium text-yellow-700">Cadre</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <Users className="text-primary" size={16} />
                <span className="text-sm font-medium text-gray-700">Groupe</span>
              </div>
            ),
        },
        {
          key: 'scope',
          label: 'Portée',
          sortable: true,
          align: 'center',
          width: '110px',
          render: (value: any) =>
            value === 'Plateforme' ? (
              <span className="inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                Plateforme
              </span>
            ) : (
              <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                Cabinet
              </span>
            ),
        },
        {
          key: 'products',
          label: 'Produits',
          sortable: false,
          render: (value: any) => {
            const items = (value as IdLabel[] | undefined) ?? [];
            if (items.length === 0) {
              return <span className="text-gray-400">-</span>;
            }
            return (
              <MoreBubble
                items={items.map((p) => ({ label: p.label }))}
              />
            );
          },
        },
        {
          key: 'subscriberName',
          label: 'Souscripteur',
          sortable: true,
          render: (value: any) => (
            <span className="text-sm text-gray-700">{value || '-'}</span>
          ),
        },
        {
          key: 'mainDueDate',
          label: 'Échéance',
          sortable: false,
          width: '140px',
          align: 'center',
          render: (value: any) => (
            <span className="text-sm text-gray-700">{value || '-'}</span>
          ),
        },
        {
          key: 'activeContractsCount',
          label: 'Contrats actifs',
          sortable: true,
          align: 'center',
          width: '120px',
          render: (value: any) => (
            <div className="flex items-center justify-center gap-2">
              <FileSignature className="text-primary" size={16} />
              <span className="font-semibold text-gray-900">{String(value ?? 0)}</span>
            </div>
          ),
        },
        {
          key: 'creationTime',
          label: 'Créé le',
          sortable: true,
          width: '120px',
          render: (value: any) => (
            <span className="text-sm text-gray-600">{formatDate(value)}</span>
          ),
        },
      ]}
      title={{
        iconKey: 'folder-open',
        label: t('SIDEBAR.MY_COLLECTIVE_CONTRACTS'),
      }}
      // L'œil (consultation) suit la permission de lecture qui a affiché la
      // liste — une session gouvernance en lecture seule doit pouvoir ouvrir un
      // contrat. Création et menu d'actions restent gatés par la matrice centrale
      // (Souscription collective / Souscription plateforme).
      actions={{
        ...(canGroupActions && {
          create: {
            label: t('GROUP_CONTRACT_PROCESS.NEW_CONTRACT_MODAL.BUTTON_LABEL'),
            onClick: handleCreate,
          },
        }),
        row: [
          {
            label: 'seeMore',
            render: (row: GroupContractItemDto, index: number) => (
              <Button
                key={index}
                color="slate"
                round
                onClick={() => handleView(row)}
              >
                <EyeIcon />
              </Button>
            ),
          },
          ...(canGroupActions ? [{
            label: 'moreMenu',
            render: (_row: GroupContractItemDto, index: number) => (
              <DropdownButton
                key={index}
                width="w-64"
                anchor="bottom"
                items={[]}
              />
            ),
          }] : []),
        ],
      }}
      selectable={false}
      pageSize={10}
      pageSizeOptions={[5, 10, 20, 50]}
      showPaginationInfo
      enableSearch
      searchPlaceholder="Rechercher un contrat collectif..."
      skeletonRowCount={8}
    />
  );
}
